import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api, toApiError, unwrap, unwrapPage } from '@/api/client';
import type { components, operations } from '@/api/schema';

export type LicenciaItem = components['schemas']['MembershipListItem'];
export type LicenciaDetalle = components['schemas']['MembershipDetail'];
export type LicenciaBody = components['schemas']['MembershipCreateBody'];
export type PermisoCatalogo = components['schemas']['PermissionCatalogItem'];
export type Cupon = components['schemas']['Coupon'];
export type CuponBody = components['schemas']['CouponCreateBody'];
export type Beca = components['schemas']['Scholarship'];
export type BecaBody = components['schemas']['ScholarshipCreateBody'];
export type Criterios = components['schemas']['ScholarshipCriteria'];
export type Equipo = components['schemas']['PurchaseGroup'];
export type FiltrosCupones = NonNullable<operations['getAdminCoupons']['parameters']['query']>;

export type VistaLicencias = NonNullable<
  NonNullable<operations['getAdminMemberships']['parameters']['query']>['view']
>;

/**
 * Pestañas del listado, como en el dashboard viejo: las licencias para instructores van aparte
 * (dan acceso a contenidos para validarlos y no se venden en la tienda).
 */
export const VISTAS_LICENCIAS: readonly (readonly [VistaLicencias, string])[] = [
  ['active', 'Activas'],
  ['instructor', 'Para instructores'],
  ['inactive', 'Inactivas'],
  ['all', 'Todas'],
];

/** Valor de `?vista=` en la URL → pestaña (por omisión, Activas). */
export function vistaLicencias(valor: string | null | undefined): VistaLicencias {
  return VISTAS_LICENCIAS.find(([k]) => k === valor)?.[0] ?? 'active';
}

export function useLicencias(query: { q?: string; view?: VistaLicencias }) {
  return useQuery({
    queryKey: ['tienda', 'licencias', query],
    queryFn: async () => {
      const { data, response } = await api.GET('/api/v1/admin/memberships', {
        params: { query },
      });
      if (!response.ok || !data) throw await toApiError(response);
      // `meta.counts`: total por pestaña con la búsqueda aplicada.
      return { filas: data.data, conteos: data.meta.counts ?? {} };
    },
    placeholderData: keepPreviousData,
  });
}

export function useLicencia(id: number | null) {
  return useQuery({
    queryKey: ['tienda', 'licencia', id],
    queryFn: () =>
      unwrap<LicenciaDetalle>(
        api.GET('/api/v1/admin/memberships/{id}', { params: { path: { id: String(id) } } }),
      ),
    enabled: id !== null,
  });
}

export function useCatalogoPermisos() {
  return useQuery({
    queryKey: ['tienda', 'permisos'],
    queryFn: () =>
      unwrap<PermisoCatalogo[]>(api.GET('/api/v1/admin/memberships/permission-catalog')),
    staleTime: Infinity,
  });
}

export function useCupones(query: FiltrosCupones) {
  return useQuery({
    queryKey: ['tienda', 'cupones', query],
    queryFn: () => unwrapPage<Cupon[]>(api.GET('/api/v1/admin/coupons', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

export function useBecas() {
  return useQuery({
    queryKey: ['tienda', 'becas'],
    queryFn: () => unwrap<Beca[]>(api.GET('/api/v1/admin/scholarships')),
  });
}

export function useEquipos(query: { page: number; status?: 'pending' | 'partial' | 'paid' }) {
  return useQuery({
    queryKey: ['tienda', 'equipos', query],
    queryFn: () =>
      unwrapPage<Equipo[]>(
        api.GET('/api/v1/admin/purchase-groups', { params: { query: { ...query, pageSize: 25 } } }),
      ),
    placeholderData: keepPreviousData,
  });
}

export const ESTADO_PROMO: Record<string, [string, string]> = {
  current: ['Vigente', 'insignia-activa'],
  scheduled: ['Programado', ''],
  expired: ['Vencido', ''],
  exhausted: ['Agotado', 'insignia-exclusivo'],
};

/** Mismo formato que valida la API para el slug público (`/comprar/{slug}/`). */
export const SLUG_LICENCIA = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Sugerencia de slug a partir del nombre: minúsculas, sin acentos, espacios → guiones. */
export function slugDe(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

/** Errores de los campos del sitio público (vacío si se puede guardar). */
export function erroresSitioLicencia(f: LicenciaBody): { slug?: string; installments?: string } {
  const errores: { slug?: string; installments?: string } = {};
  const slug = f.slug ?? '';
  if (slug && (slug.length < 3 || slug.length > 60 || !SLUG_LICENCIA.test(slug))) {
    errores.slug =
      'De 3 a 60 caracteres: minúsculas, números y guiones (sin guion al inicio o al final).';
  }
  const meses = f.installments;
  if (meses != null && (!Number.isInteger(meses) || meses < 2 || meses > 24)) {
    errores.installments = 'Debe ser un número entero de 2 a 24.';
  }
  return errores;
}

/** Cuerpo listo para POST/PATCH: los campos del sitio vacíos se mandan como `null` (para borrarlos). */
export function cuerpoLicencia(f: LicenciaBody): LicenciaBody {
  return {
    ...f,
    slug: f.slug?.trim() || null,
    installments: f.installments ?? null,
    accessEndsOn: f.accessEndsOn || null,
  };
}
